import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';

type ItemMapper<BackendType, FrontendType, Args = unknown> = (
    item: BackendType,
    meta: FetchBaseQueryMeta | undefined,
    arg: Args,
) => FrontendType;

type TransformResponseFn<BackendType, FrontendType, Args = unknown> = (
    baseQueryReturnValue: BackendType,
    meta: FetchBaseQueryMeta | undefined,
    arg: Args,
) => FrontendType;

export function createDataMapper<BackendType, FrontendType, Args = unknown>(
    mapper: ItemMapper<BackendType, FrontendType, Args>,
    isArray?: false,
): TransformResponseFn<BackendType, FrontendType, Args>;

export function createDataMapper<BackendType, FrontendType, Args = unknown>(
    mapper: ItemMapper<BackendType, FrontendType, Args>,
    isArray?: true,
): TransformResponseFn<BackendType[], FrontendType[], Args>;

export function createDataMapper<BackendType, FrontendType, Args = unknown>(
    mapper: ItemMapper<BackendType, FrontendType, Args>,
    isArray?: boolean,
): TransformResponseFn<BackendType, FrontendType | FrontendType[], Args> {
    return (
        baseQueryReturnValue: BackendType | BackendType[],
        meta: FetchBaseQueryMeta | undefined,
        arg: Args,
    ): FrontendType | FrontendType[] => {
        if (isArray) {
            const response = baseQueryReturnValue as BackendType[];
            return response.map((response) =>
                mapper(response, meta, arg as Args),
            );
        }
        const response = baseQueryReturnValue as BackendType;
        return mapper(response, meta, arg as Args);
    };
}

export type MapperKeys<Source extends object, Target extends object> = {
    [key in keyof Partial<Target>]: keyof Source;
};

export function transformSourceToTargetKeys<
    Source extends object,
    Target extends object,
>(source: Source, keyMap: MapperKeys<Source, Target>) {
    return Object.entries<keyof Source>(keyMap)
        .map(([key, value]) => ({
            [key]: source[value],
        }))
        .reduce((prev, current) => ({
            ...prev,
            ...current,
        })) as Target;
}

export function transformTargetToSourceKeys<
    Source extends object,
    Target extends object,
>(target: Target, keyMap: MapperKeys<Source, Target>) {
    return Object.entries<keyof Source>(keyMap)
        .map(([key, value]) => ({
            [value]: target[key as keyof Target],
        }))
        .reduce((prev, current) => ({
            ...prev,
            ...current,
        })) as Source;
}
